import React, {
    createContext,
    useCallback,
    useContext,
    useState,
    type ReactNode,
} from 'react';

import type { FileObject } from '../classes';

interface FileQueues {
    uploadQueue: Record<string, Array<FileObject>>;
    downloadQueue: Record<string, Record<string, FileObject>>;
}

interface IFileContext {
    uploadQueue: Record<string, Array<FileObject>>;
    downloadQueue: Record<string, Record<string, FileObject>>;
    addToUploadQueue: (file: FileObject) => void;
    removeFromQueue: (file: FileObject) => void;
    addToDownloadQueue: (file: FileObject) => void;
    clearQueues: () => void;
}

const FileContext = createContext<IFileContext | undefined>(undefined);

export const useFileContext = () => {
    const context = useContext(FileContext);
    if (!context) {
        throw new Error('useFileContext must be used within FileProvider');
    }
    return context;
};

interface IFileProviderProps {
    children: ReactNode;
}

export const FileProvider: React.FC<IFileProviderProps> = ({ children }) => {
    const [queues, setQueues] = useState<FileQueues>({
        uploadQueue: {},
        downloadQueue: {},
    });

    const addToUploadQueue = useCallback((file: FileObject) => {
        setQueues((prev) => {
            const uploadQueue = { ...prev.uploadQueue };
            const queueName = file.queueName || 'default';
            if (!uploadQueue[queueName]) {
                uploadQueue[queueName] = [];
            }
            uploadQueue[queueName] = [...uploadQueue[queueName], file];
            return { ...prev, uploadQueue };
        });
    }, []);

    const removeFromQueue = useCallback((file: FileObject) => {
        setQueues((prev) => {
            const uploadQueue = { ...prev.uploadQueue };
            const downloadQueue = { ...prev.downloadQueue };

            if (file?.queueName) {
                const { local, queueName, pathIndex } = file;

                if (local) {
                    if (uploadQueue[queueName]) {
                        uploadQueue[queueName] = uploadQueue[queueName].filter(
                            (queueFile: FileObject) => queueFile.id !== file.id,
                        );
                    }
                } else if (downloadQueue[queueName]?.[pathIndex as string]) {
                    const existingFile =
                        downloadQueue[queueName][pathIndex as string];
                    const newQueueForName = { ...downloadQueue[queueName] };
                    newQueueForName[pathIndex as string] = {
                        ...existingFile,
                        toDelete: !existingFile.toDelete,
                    } as FileObject;
                    downloadQueue[queueName] = newQueueForName;
                }
            }

            return { uploadQueue, downloadQueue };
        });
    }, []);

    const addToDownloadQueue = useCallback((file: FileObject) => {
        setQueues((prev) => {
            const downloadQueue = { ...prev.downloadQueue };

            if (file?.pathIndex) {
                const queueName = file.queueName || 'default';
                const newQueueForName = {
                    ...(downloadQueue[queueName] || {}),
                };
                newQueueForName[file.pathIndex] = file;
                downloadQueue[queueName] = newQueueForName;
            }

            return { ...prev, downloadQueue };
        });
    }, []);

    const clearQueues = useCallback(() => {
        setQueues({
            uploadQueue: {},
            downloadQueue: {},
        });
    }, []);

    return (
        <FileContext.Provider
            value={{
                uploadQueue: queues.uploadQueue,
                downloadQueue: queues.downloadQueue,
                addToUploadQueue,
                removeFromQueue,
                addToDownloadQueue,
                clearQueues,
            }}
        >
            {children}
        </FileContext.Provider>
    );
};
